// components/tier-branch/AutoAssignDialog.tsx
// "Tier Wise Manage User" — previews (dry-run) which users would move based on
// their precomputed transaction rollup, then commits on confirm. Moves both the
// user's tier AND the active branch of that tier (writes users.tier_id +
// users.branch_id, with tier_assignments / branch_transfers audit rows).
import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, Chip,
} from "@mui/material";
import * as api from "../../services/tierBranchApi";
import type { AutoAssignResult } from "../../types/tierBranch";
import type { Notify } from "./useSnack";

const AutoAssignDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCommitted: () => void;
  notify: Notify;
}> = ({ open, onClose, onCommitted, notify }) => {
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState<AutoAssignResult | null>(null);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      return;
    }
    let alive = true;
    setLoading(true);
    api
      .runTierAutoAssign({ dryRun: true })
      .then((r) => {
        if (alive) setPreview(r);
      })
      .catch((e: any) => {
        if (alive) notify(e?.message || "Failed to preview", "error");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, notify]);

  const handleCommit = async () => {
    setCommitting(true);
    try {
      const r = await api.runTierAutoAssign({});
      notify(`Auto-assign complete: ${r.summary.moved} user(s) moved`, "success");
      onCommitted();
      onClose();
    } catch (e: any) {
      notify(e?.message || "Auto-assign failed", "error");
    } finally {
      setCommitting(false);
    }
  };

  // Backend only returns rows where the tier and/or branch actually changes.
  const moves = preview?.assignments ?? [];

  return (
    <Dialog open={open} onClose={committing ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Tier Wise Manage User</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : preview ? (
          <Box>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <Chip color="primary" label={`${preview.summary.moved} will move`} />
              <Chip label={`${preview.summary.unchanged} unchanged`} />
            </Box>
            {moves.length === 0 ? (
              <Typography color="text.secondary">
                Every user is already in the correct tier — nothing to move.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>From Tier</TableCell>
                    <TableCell>To Tier</TableCell>
                    <TableCell>From Branch</TableCell>
                    <TableCell>To Branch</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {moves.slice(0, 200).map((a) => (
                    <TableRow key={a.userId}>
                      <TableCell>{a.username ?? a.userId}</TableCell>
                      <TableCell>{a.fromTierId ?? "—"}</TableCell>
                      <TableCell>{a.toTierId ?? "—"}</TableCell>
                      <TableCell>{a.fromBranchId ?? "—"}</TableCell>
                      <TableCell>{a.toBranchId ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {moves.length > 200 && (
              <Typography variant="caption" color="text.secondary">
                Showing first 200 of {moves.length} moves.
              </Typography>
            )}
          </Box>
        ) : (
          <Typography color="text.secondary">No preview available.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={committing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCommit}
          disabled={loading || committing || moves.length === 0}
          startIcon={committing ? <CircularProgress size={16} /> : undefined}
        >
          Confirm & Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoAssignDialog;
